/**
 * Version Manager - Handles client-side version checking and cache busting
 * Works with the version API to ensure users always have the latest code
 */

import { store } from '../store/store';
import { fetchSystemVersion } from '../store/thunks/versionThunk';

class VersionManager {
  constructor() {
    this.currentVersion = null;
    this.serverVersion = null;
    this.checkInterval = null;
    this.CHECK_INTERVAL_MS = 5 * 60 * 1000; // Check every 5 minutes
    this.STORAGE_KEY = 'simone_app_version';
    
    console.log('[Version Manager] Initializing version management system');
    this.init();
  }

  async init() {
    try {
      console.log('[Version Manager] 🚀 Starting initialization...');
      
      // Get current version from build-time
      this.currentVersion = this.getBuildVersion();
      
      // Get stored version from previous session
      const storedVersion = localStorage.getItem(this.STORAGE_KEY);
      
      console.log(`[Version Manager] Current build version: ${this.currentVersion}`);
      console.log(`[Version Manager] Stored version: ${storedVersion}`);
      console.log(`[Version Manager] Environment version: ${process.env.REACT_APP_VERSION || 'not set'}`);
      
      // Check server version
      await this.checkServerVersion();
      
      // If versions don't match, handle cache invalidation
      if (storedVersion && storedVersion !== this.currentVersion) {
        console.log(`[Version Manager] Version mismatch detected - stored: ${storedVersion}, current: ${this.currentVersion}`);
        this.handleVersionMismatch(storedVersion, this.currentVersion);
      }
      
      // Store current version
      localStorage.setItem(this.STORAGE_KEY, this.currentVersion);
      
      // Start periodic version checking
      this.startVersionChecking();
      
      console.log('[Version Manager] ✅ Initialization complete');
    } catch (error) {
      console.error('[Version Manager] ❌ Initialization failed:', error);
    }
  }

  getBuildVersion() {
    // Try to get version from process.env first (build-time)
    if (process.env.REACT_APP_VERSION) {
      console.log(`[Version Manager] Using environment version: ${process.env.REACT_APP_VERSION}`);
      return process.env.REACT_APP_VERSION;
    }
    
    // In production, prefer using server version as client version
    // since webpack can corrupt the require() statement
    if (process.env.NODE_ENV === 'production') {
      console.log('[Version Manager] Production mode - will use server version as client version');
      return null; // Will be set by checkServerVersion
    }
    
    // Fallback to package.json version (development)
    try {
      const packageJson = require('../../package.json');
      console.log(`[Version Manager] Package.json loaded:`, typeof packageJson, Object.keys(packageJson).slice(0, 5));
      
      if (packageJson && typeof packageJson.version === 'string' && packageJson.version.length > 0) {
        console.log(`[Version Manager] Using package.json version: ${packageJson.version}`);
        return packageJson.version;
      } else {
        console.warn('[Version Manager] Package.json version is invalid:', typeof packageJson.version, packageJson.version);
        return '1.0.0';
      }
    } catch (error) {
      console.warn('[Version Manager] Could not get build version, using fallback:', error);
      return '1.0.0';
    }
  }

  async checkServerVersion() {
    try {
      // Use Redux thunk to get server version
      const resultAction = await store.dispatch(fetchSystemVersion());
      
      if (fetchSystemVersion.fulfilled.match(resultAction)) {
        this.serverVersion = resultAction.payload.version;
        console.log(`[Version Manager] Server version: ${this.serverVersion}`);
        
        // In production, if client version is null, use server version
        if (this.currentVersion === null && process.env.NODE_ENV === 'production') {
          console.log('[Version Manager] Setting client version to match server version in production');
          this.currentVersion = this.serverVersion;
          localStorage.setItem(this.STORAGE_KEY, this.currentVersion);
          return; // No version mismatch in this case
        }
        
        // Check if client and server versions match
        console.log(`[Version Manager] Version comparison - client: "${this.currentVersion}" (${typeof this.currentVersion}), server: "${this.serverVersion}" (${typeof this.serverVersion})`);
        
        if (this.currentVersion !== this.serverVersion) {
          console.warn(`[Version Manager] Client-server version mismatch - client: ${this.currentVersion}, server: ${this.serverVersion}`);
          this.handleVersionMismatch(this.currentVersion, this.serverVersion);
        }
      } else {
        console.error('[Version Manager] Failed to fetch server version:', resultAction.error);
      }
    } catch (error) {
      console.error('[Version Manager] Error checking server version:', error);
    }
  }

  handleVersionMismatch(oldVersion, newVersion) {
    console.warn(`[Version Manager] Handling version mismatch: ${oldVersion} → ${newVersion}`);
    
    // Check if we've already notified about this version to prevent infinite loops
    const lastNotifiedVersion = localStorage.getItem('last_notified_version');
    if (lastNotifiedVersion === newVersion) {
      console.log('[Version Manager] Already notified about this version, skipping notification');
      return;
    }
    
    // Store that we've notified about this version
    localStorage.setItem('last_notified_version', newVersion);
    
    // Clear various caches
    this.clearApplicationCache();
    
    // Show user notification about update
    this.showUpdateNotification(oldVersion, newVersion);
  }

  clearApplicationCache() {
    console.log('[Version Manager] Clearing application caches');
    
    try {
      // Clear localStorage (except user preferences and notification tracking)
      const preserveKeys = ['user_preferences', 'auth_token', 'remember_me', 'last_notified_version'];
      const keysToRemove = [];
      
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && !preserveKeys.some(preserve => key.includes(preserve))) {
          keysToRemove.push(key);
        }
      }
      
      keysToRemove.forEach(key => {
        localStorage.removeItem(key);
        console.log(`[Version Manager] Cleared localStorage key: ${key}`);
      });
      
      // Clear sessionStorage
      sessionStorage.clear();
      console.log('[Version Manager] Cleared sessionStorage');
      
      // Clear service worker cache if available
      if ('serviceWorker' in navigator && 'caches' in window) {
        caches.keys().then(cacheNames => {
          cacheNames.forEach(cacheName => {
            caches.delete(cacheName);
            console.log(`[Version Manager] Cleared cache: ${cacheName}`);
          });
        });
      }
      
    } catch (error) {
      console.error('[Version Manager] Error clearing caches:', error);
    }
  }

  showUpdateNotification(oldVersion, newVersion) {
    console.log(`[Version Manager] 🎉 SHOWING UPDATE NOTIFICATION: ${oldVersion} → ${newVersion}`);
    
    // Create a persistent notification for users
    const notification = document.createElement('div');
    notification.innerHTML = `
      <div style="
        position: fixed;
        top: 20px;
        right: 20px;
        background: #10B981;
        color: white;
        padding: 16px 20px;
        border-radius: 8px;
        box-shadow: 0 4px 12px rgba(0,0,0,0.15);
        z-index: 10000;
        font-family: system-ui, -apple-system, sans-serif;
        font-size: 14px;
        max-width: 350px;
        animation: slideIn 0.3s ease-out;
      ">
        <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 8px;">
          <div style="font-weight: 600;">🚀 Simone Updated!</div>
          <button id="closeNotification" style="
            background: transparent; 
            border: none; 
            color: white; 
            cursor: pointer; 
            font-size: 16px;
            padding: 0;
            margin-left: 10px;
          ">✕</button>
        </div>
        <div style="opacity: 0.9; margin-bottom: 12px;">
          Version ${newVersion} is now available.
        </div>
        <button id="refreshButton" style="
          background: rgba(255,255,255,0.2);
          border: 1px solid rgba(255,255,255,0.3);
          color: white;
          padding: 8px 16px;
          border-radius: 4px;
          cursor: pointer;
          font-size: 13px;
          font-weight: 500;
        ">
          Refresh Now
        </button>
      </div>
    `;
    
    document.body.appendChild(notification);
    
    // Refresh functionality
    const refreshApp = () => {
      console.log('[Version Manager] Refreshing application for version update');
      window.location.reload(true);
    };
    
    // Close functionality  
    const closeNotification = () => {
      console.log('[Version Manager] User dismissed update notification');
      document.body.removeChild(notification);
    };
    
    // Add event listeners
    notification.querySelector('#refreshButton').addEventListener('click', refreshApp);
    notification.querySelector('#closeNotification').addEventListener('click', closeNotification);
    
    // No auto-refresh - let user decide
  }

  startVersionChecking() {
    // Check version every 5 minutes
    this.checkInterval = setInterval(() => {
      console.log('[Version Manager] Performing periodic version check');
      this.checkServerVersion();
    }, this.CHECK_INTERVAL_MS);
    
    console.log(`[Version Manager] Started periodic version checking (every ${this.CHECK_INTERVAL_MS/1000/60} minutes)`);
  }

  stopVersionChecking() {
    if (this.checkInterval) {
      clearInterval(this.checkInterval);
      this.checkInterval = null;
      console.log('[Version Manager] Stopped periodic version checking');
    }
  }

  // Manual version check method
  async forceVersionCheck() {
    console.log('[Version Manager] Forcing version check');
    await this.checkServerVersion();
  }

  // Get current version info
  getVersionInfo() {
    return {
      current: this.currentVersion,
      server: this.serverVersion,
      lastCheck: new Date().toISOString()
    };
  }
}

// Create singleton instance
export const versionManager = new VersionManager();

// Make available globally for debugging
if (typeof window !== 'undefined') {
  window.versionManager = versionManager;
}

// Export for use in components
export default versionManager;